import React from 'react';
import SlideLayout, { SlideTitle, SlideContent } from '../components/SlideLayout';
import { Lightbulb, TrendingUp, MessageSquare } from 'lucide-react';

const AhaMomentSlide = () => {
  return (
    <SlideLayout maxWidth="6xl">
      <SlideTitle 
        subtitle="The emergence of self-reflection and error correction during RL training"
      >
        The "Aha Moment" Phenomenon
      </SlideTitle>
      
      <SlideContent>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: Explanation */}
          <div>
            <div className="slide-card mb-4 border-chart-4/30">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-full bg-chart-4/20 flex items-center justify-center">
                  <Lightbulb className="w-5 h-5" style={{ color: 'hsl(var(--chart-4))' }} />
                </div>
                <div>
                  <h3 className="font-heading font-semibold text-foreground">What Happened at Step 8,000?</h3>
                  <p className="text-xs text-muted-foreground">DeepSeek-R1-Zero training</p>
                </div>
              </div>
              
              <p className="text-sm text-muted-foreground mb-4">
                A pivotal moment where the model's behavior <strong className="text-foreground">shifted qualitatively</strong>:
              </p>
              
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <TrendingUp className="w-4 h-4 mt-0.5" style={{ color: 'hsl(var(--chart-4))' }} />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Sudden spike in "wait" usage</p>
                    <p className="text-xs text-muted-foreground">Model began pausing mid-generation</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MessageSquare className="w-4 h-4 mt-0.5" style={{ color: 'hsl(var(--chart-4))' }} />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Reflective vocabulary emerged</p>
                    <p className="text-xs text-muted-foreground">"check", "reevaluate", "wait"</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Lightbulb className="w-4 h-4 mt-0.5" style={{ color: 'hsl(var(--chart-4))' }} />
                  <div>
                    <p className="text-sm font-semibold text-foreground">Self-correction behavior</p>
                    <p className="text-xs text-muted-foreground">Model fixes its own mistakes mid-reasoning</p>
                  </div>
                </li>
              </ul>
            </div>
            
            <div className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, hsla(45, 93%, 58%, 0.1), hsla(45, 93%, 58%, 0.05))', border: '1px solid hsla(45, 93%, 58%, 0.2)' }}>
              <p className="text-sm">
                <strong style={{ color: 'hsl(var(--chart-4))' }}>Significance:</strong>
                <span className="text-muted-foreground"> This was NOT programmed or taught. The model discovered that self-reflection improves final answers through pure reward optimization.</span>
              </p>
            </div>
          </div>
          
          {/* Right: Example trajectory */}
          <div className="slide-card" style={{ background: 'hsl(222, 47%, 7%)' }}>
            <h3 className="font-heading font-semibold text-foreground mb-4 flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-primary" />
              Example: "Aha" Trajectory
            </h3>
            
            <div className="space-y-3 font-mono text-sm">
              <div className="p-3 rounded-lg bg-muted/30">
                <p className="text-xs text-muted-foreground mb-1">Initial attempt:</p>
                <p className="text-muted-foreground">"Let me calculate... 24 × 3 = 72..."</p>
              </div>
              
              <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                <p className="text-xs text-destructive mb-1">Error detected:</p>
                <p className="text-muted-foreground">"...so the answer is 72."</p>
                <p className="text-xs text-destructive mt-1">(Wrong approach)</p>
              </div>
              
              <div className="p-3 rounded-lg border-2" style={{ background: 'hsla(45, 93%, 58%, 0.1)', borderColor: 'hsla(45, 93%, 58%, 0.4)' }}>
                <p className="text-xs mb-1" style={{ color: 'hsl(var(--chart-4))' }}>💡 The "Aha" moment:</p>
                <p className="text-foreground italic">
                  "Wait, wait. Wait. That's an aha moment I need to reevaluate..."
                </p>
              </div>
              
              <div className="p-3 rounded-lg bg-accent/10 border border-accent/20">
                <p className="text-xs text-accent mb-1">Self-correction:</p>
                <p className="text-muted-foreground">"Actually, I should approach this differently. Let me reconsider..."</p>
              </div>
              
              <div className="p-3 rounded-lg bg-accent/20 border border-accent/30">
                <p className="text-xs text-accent mb-1">✓ Correct solution:</p>
                <p className="text-foreground">"The correct answer is 48."</p>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-6 grid grid-cols-3 gap-4 text-center">
          <div className="p-3 rounded-lg bg-muted/30">
            <p className="text-2xl font-mono text-primary">~8,000</p>
            <p className="text-xs text-muted-foreground">Training step when "Aha" emerged</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/30">
            <p className="text-2xl font-mono text-accent">5x</p>
            <p className="text-xs text-muted-foreground">Increase in accuracy after emergence</p>
          </div>
          <div className="p-3 rounded-lg bg-muted/30">
            <p className="text-2xl font-mono" style={{ color: 'hsl(var(--chart-4))' }}>0</p>
            <p className="text-xs text-muted-foreground">Human examples needed to teach this</p>
          </div>
        </div>
      </SlideContent>
    </SlideLayout>
  );
};

export default AhaMomentSlide;
